import { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Sidebar } from "./components/Sidebar";
import { ModeBottomBar } from "./components/ModeBottomBar";
import { UpdateBanner } from "./components/UpdateBanner";
import { StaleSourceBanner } from "./components/StaleSourceBanner";
import { InstallButton } from "./components/InstallButton";
import { WhatsNewToast } from "./components/WhatsNewToast";
import { CommandPalette } from "./components/CommandPalette";
import { QuickCaptureFab } from "./components/QuickCaptureFab";
import { FeatureIntroOverlay } from "./components/FeatureIntroOverlay";
import { Today } from "./pages/Today";
import { Attention } from "./pages/Attention";
import { People } from "./pages/People";
import { Library } from "./pages/Library";
import { Recall } from "./pages/Recall";
import { Capture } from "./pages/Capture";
import { Explore } from "./pages/Explore";
import { Settings } from "./pages/Settings";
import { Login } from "./pages/Login";
import { Onboarding } from "./pages/Onboarding";
import { Backlog } from "./pages/Backlog";
import { OrgDetail } from "./pages/OrgDetail";
import { Timeline } from "./pages/Timeline";
import { Wishes } from "./pages/Wishes";
import { Things } from "./pages/Things";
import { TeachingHelp } from "./pages/TeachingHelp";
import { CulturalProfile } from "./pages/CulturalProfile";
import { Composer } from "./pages/Composer";
import { Me } from "./pages/Me";
import { SelfIdentity } from "./pages/SelfIdentity";
import { PersonDossier } from "./pages/PersonDossier";
import { Places } from "./pages/Places";
import { PlaceDossier } from "./pages/PlaceDossier";
import { TripDetail } from "./pages/TripDetail";
import { EngagementDetail } from "./pages/Engagements";
import { CompanionPair } from "./pages/CompanionPair";
import { IdentityReview } from "./pages/IdentityReview";
import { Subscriptions } from "./pages/Subscriptions";
import { Reflect } from "./pages/Reflect";
import { ReflectHistory } from "./pages/ReflectHistory";
import { OutlookBridgeTest } from "./pages/OutlookBridgeTest";
import { OutlookBridgeConfigure } from "./pages/OutlookBridgeConfigure";
// Mail + Calendar + Tasks moved to WakiOutlookViewer v2 in Phase 5
// (2026-05-14). Memso reads via the mail-viewer connector; the mail
// app UI lives on https://mail.memso.ai (separate Pages project).
import { Status } from "./pages/Status";
import { Perf } from "./pages/Perf";
import { AiFeaturesSettings } from "./pages/AiFeaturesSettings";
import { SettingsAi } from "./pages/SettingsAi";
import { SettingsPrompts } from "./pages/SettingsPrompts";
import { SettingsAgentRuns } from "./pages/SettingsAgentRuns";
import { ContextSettings } from "./pages/ContextSettings";
import { useContextPing } from "./lib/useContextPing";
import { Teams } from "./pages/Teams";
import { WhatsApp } from "./pages/WhatsApp";
import { AndroidMessages } from "./pages/AndroidMessages";
import { LinkedIn } from "./pages/LinkedIn";
import { SimplifiConfigure } from "./pages/SimplifiConfigure";
import { Preferences } from "./pages/Preferences";
import { Routines } from "./pages/Routines";
import { Decisions } from "./pages/Decisions";
import { Health } from "./pages/Health";
import { Patterns } from "./pages/Patterns";
import { useAuth } from "./lib/auth";
import { ViewProvider, useView } from "./lib/viewContext";
import { ViewSelector } from "./components/ViewSelector";
import { PacketViewerProvider } from "./components/PacketViewerContext";
import { L2NavProvider } from "./components/L2NavContext";
import { L2Slot } from "./components/L2Nav";

export function App() {
  return (
    <BrowserRouter>
      <PacketViewerProvider>
        <L2NavProvider>
          <Shell />
        </L2NavProvider>
      </PacketViewerProvider>
    </BrowserRouter>
  );
}

/** Inside-Router shell. Routes that own their own canvas (sign-in,
 *  onboarding) skip the cockpit chrome. The path-based `/u/:handle`
 *  and the bare `/` route both resolve to the cockpit; the bare
 *  route auto-redirects to `/u/<my-handle>` so the address bar
 *  always reads cleanly. */
function Shell() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const auth = useAuth();
  // Post-OAuth scope gate. The Google connector callback is a full
  // server redirect, so a fresh sign-in / new-grant lands here as a
  // fresh App mount. If any connector instance is parked on the
  // scope picker (pending_scope_picker=1), route the user to the
  // first-class Configure step inside the connectors sub-page
  // before anything ingests. Runs once per mount; best-effort so a
  // missing endpoint never strands.
  const scopeGateChecked = useRef(false);
  useEffect(() => {
    if (auth.loading || !auth.authenticated) return;
    if (scopeGateChecked.current) return;
    if (pathname.includes("/settings/connectors/configure-google")) return;
    scopeGateChecked.current = true;
    fetch("/api/connectors/pending-scope", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && Array.isArray(d.pending) && d.pending.length > 0)
          nav("/settings/connectors/configure-google", { replace: true });
      })
      .catch(() => {});
  }, [auth.loading, auth.authenticated, pathname, nav]);
  // Track C Layer 1: time-only context ping every 5 min while signed
  // in. Location attaches only with an explicit browser grant; the
  // hook never triggers a permission prompt on its own.
  useContextPing(!auth.loading && auth.authenticated);
  const isLogin = pathname.startsWith("/login");
  const isOnboarding = pathname.startsWith("/onboarding");

  if (isLogin) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }
  if (isOnboarding) {
    return (
      <Routes>
        <Route path="/onboarding" element={<Onboarding />} />
      </Routes>
    );
  }

  // Logged-out: skip cockpit chrome entirely. The Login canvas
  // (rendered by RootRoute) is full-bleed self-contained, so the
  // header / bottom-nav / install-FAB / command-palette would all
  // be visual noise + leak signed-in intent (see fix list 2-5,
  // 2026-04-30). Auth-gated routes (Capture / Explore / Settings)
  // would 401 on their API calls anyway; route them to the bare
  // RootRoute so the Login canvas appears regardless of where the
  // logged-out user landed.
  if (!auth.loading && !auth.authenticated) {
    return <Login />;
  }

  return (
    <ViewProvider>
      <UpdateBanner />
      <StaleSourceBanner />
      <WhatsNewToast />
      <FeatureIntroOverlay />
      <Sidebar />
      <L2Slot />
      <ModeBottomBar />
      <ViewSelector />
      <InstallButton />
      <CommandPalette />
      <QuickCaptureFab />
      <ViewScopedRoutes />
    </ViewProvider>
  );
}

/** The routed page subtree, remounted whenever the active view
 *  changes. The app fetches with plain fetch+useEffect (no query
 *  cache), so remounting the current page is the reliable way to make
 *  every surface re-pull data against the new server-side scope
 *  filter. */
function ViewScopedRoutes() {
  const { epoch } = useView();
  return (
    <main
      key={epoch}
      id="main-content"
      className="min-h-screen pt-14 md:pt-0 pb-20 md:pb-6 md:ml-[calc(var(--bv2-sidebar-w,56px)+var(--bv2-l2-w,0px))] transition-[margin-left] duration-200 ease-out motion-reduce:transition-none"
    >
      <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/u/:handle" element={<UserWorkspace />} />
          <Route path="/u/:handle/capture" element={<Capture />} />
          <Route path="/u/:handle/explore" element={<Explore />} />
          <Route path="/u/:handle/settings" element={<Settings />} />
          <Route path="/u/:handle/settings/integrations/outlook-bridge" element={<OutlookBridgeTest />} />
          <Route path="/u/:handle/settings/connectors/outlook-bridge" element={<OutlookBridgeConfigure />} />
          <Route path="/u/:handle/settings/connectors/android-messages" element={<AndroidMessages />} />
          <Route path="/u/:handle/settings/connectors/linkedin" element={<LinkedIn />} />
          <Route path="/u/:handle/settings/connectors/simplifi" element={<SimplifiConfigure />} />
          <Route path="/u/:handle/settings/connectors/:subsection" element={<Settings />} />
          <Route path="/u/:handle/settings/connectors" element={<Settings />} />
          <Route path="/u/:handle/settings/:section" element={<Settings />} />
          <Route path="/u/:handle/explore" element={<Navigate to="/recall" replace />} />
          <Route path="/u/:handle/explore/:lens" element={<Navigate to="/recall" replace />} />
          <Route path="/capture" element={<Capture />} />
          <Route path="/capture/quick" element={<Capture />} />
          <Route path="/explore" element={<Navigate to="/recall" replace />} />
          <Route path="/explore/library" element={<Navigate to="/library" replace />} />
          <Route path="/explore/library/:entity" element={<LibraryEntityRedirect />} />
          <Route path="/explore/:lens" element={<Navigate to="/recall" replace />} />
          <Route path="/now" element={<Navigate to="/today" replace />} />
          <Route path="/u/:handle/now" element={<Navigate to="/today" replace />} />
          <Route path="/act" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/act" element={<Navigate to="/attention" replace />} />
          <Route path="/communicate" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/communicate" element={<Navigate to="/attention" replace />} />
          <Route path="/pulse" element={<Navigate to="/today" replace />} />
          <Route path="/u/:handle/pulse" element={<Navigate to="/today" replace />} />
          <Route path="/u/:handle/capture/quick" element={<Capture />} />
          <Route path="/u/:handle/explore/library" element={<Navigate to="/library" replace />} />
          <Route path="/u/:handle/explore/library/:entity" element={<LibraryEntityRedirect />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/integrations/outlook-bridge" element={<OutlookBridgeTest />} />
          <Route path="/settings/connectors/outlook-bridge" element={<OutlookBridgeConfigure />} />
          <Route path="/settings/connectors/android-messages" element={<AndroidMessages />} />
          <Route path="/settings/connectors/linkedin" element={<LinkedIn />} />
          <Route path="/settings/connectors/simplifi" element={<SimplifiConfigure />} />
          <Route path="/settings/connectors/:subsection" element={<Settings />} />
          <Route path="/settings/connectors" element={<Settings />} />
          <Route path="/settings/help" element={<TeachingHelp />} />
          <Route path="/u/:handle/settings/help" element={<TeachingHelp />} />
          <Route path="/settings/profile/cultural" element={<CulturalProfile />} />
          <Route path="/u/:handle/settings/profile/cultural" element={<CulturalProfile />} />
          <Route path="/settings/profile/identity" element={<SelfIdentity />} />
          <Route path="/u/:handle/settings/profile/identity" element={<SelfIdentity />} />
          <Route path="/settings/:section" element={<Settings />} />
          <Route path="/backlog" element={<Backlog />} />
          <Route path="/actions/all" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/actions/all" element={<Navigate to="/attention" replace />} />
          <Route path="/actions/agentic" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/u/:handle/actions/agentic" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/actions" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/u/:handle/actions" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/intents" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/u/:handle/intents" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/forgotten" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/forgotten" element={<Navigate to="/attention" replace />} />
          <Route path="/orgs" element={<Navigate to="/people?tab=orgs" replace />} />
          <Route path="/u/:handle/orgs" element={<Navigate to="/people?tab=orgs" replace />} />
          <Route path="/orgs/:id" element={<OrgDetail />} />
          <Route path="/u/:handle/orgs/:id" element={<OrgDetail />} />
          <Route path="/flow" element={<Navigate to="/library?tab=timeline" replace />} />
          <Route path="/u/:handle/flow" element={<Navigate to="/library?tab=timeline" replace />} />
          <Route path="/agenda" element={<Navigate to="/library?tab=trips" replace />} />
          <Route path="/u/:handle/agenda" element={<Navigate to="/library?tab=trips" replace />} />
          <Route path="/agenda/trips/:id" element={<TripDetail />} />
          <Route path="/u/:handle/agenda/trips/:id" element={<TripDetail />} />
          <Route path="/timeline" element={<Timeline />} />
          <Route path="/u/:handle/timeline" element={<Timeline />} />
          <Route path="/wishes" element={<Wishes />} />
          <Route path="/u/:handle/wishes" element={<Wishes />} />
          <Route path="/things" element={<Things />} />
          <Route path="/u/:handle/things" element={<Things />} />
          <Route path="/transactions" element={<Navigate to="/library?tab=money" replace />} />
          <Route path="/u/:handle/transactions" element={<Navigate to="/library?tab=money" replace />} />
          <Route path="/needs-review" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/needs-review" element={<Navigate to="/attention" replace />} />
          <Route path="/composer" element={<Composer />} />
          <Route path="/u/:handle/composer" element={<Composer />} />
          <Route path="/actions/queue" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/u/:handle/actions/queue" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/today" element={<Today />} />
          <Route path="/today/horizon/:horizon" element={<Today />} />
          <Route path="/u/:handle/today" element={<Today />} />
          <Route path="/u/:handle/today/horizon/:horizon" element={<Today />} />
          <Route path="/engagements" element={<Navigate to="/attention" replace />} />
          <Route path="/engagements/tab/:tab" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/engagements" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/engagements/tab/:tab" element={<Navigate to="/attention" replace />} />
          <Route path="/conversations" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/conversations" element={<Navigate to="/attention" replace />} />
          <Route path="/recommendations" element={<Navigate to="/attention?filter=recommendation" replace />} />
          <Route path="/u/:handle/recommendations" element={<Navigate to="/attention?filter=recommendation" replace />} />
          <Route path="/commitments" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/commitments" element={<Navigate to="/attention" replace />} />
          <Route path="/goals" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/goals" element={<Navigate to="/attention" replace />} />
          <Route path="/engagements/:id" element={<EngagementDetail />} />
          <Route path="/u/:handle/engagements/:id" element={<EngagementDetail />} />
          <Route path="/people/needs-review" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/u/:handle/people/needs-review" element={<Navigate to="/attention?filter=action" replace />} />
          <Route path="/people/needs-attention" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/people/needs-attention" element={<Navigate to="/attention" replace />} />
          <Route path="/me" element={<Me />} />
          <Route path="/u/:handle/me" element={<Me />} />
          <Route path="/people/:id" element={<PersonDossier />} />
          <Route path="/u/:handle/people/:id" element={<PersonDossier />} />
          <Route path="/places" element={<Navigate to="/library?tab=places" replace />} />
          <Route path="/u/:handle/places" element={<Navigate to="/library?tab=places" replace />} />
          <Route path="/places/:id" element={<PlaceDossier />} />
          <Route path="/u/:handle/places/:id" element={<PlaceDossier />} />
          <Route path="/settings/companion-pair" element={<CompanionPair />} />
          <Route path="/u/:handle/settings/companion-pair" element={<CompanionPair />} />
          <Route path="/observations" element={<Navigate to="/attention" replace />} />
          <Route path="/u/:handle/observations" element={<Navigate to="/attention" replace />} />
          <Route path="/people/identity-review" element={<IdentityReview />} />
          <Route path="/u/:handle/people/identity-review" element={<IdentityReview />} />
          <Route path="/financial/subscriptions" element={<Subscriptions />} />
          <Route path="/u/:handle/financial/subscriptions" element={<Subscriptions />} />
          <Route path="/reflect" element={<Reflect />} />
          <Route path="/u/:handle/reflect" element={<Reflect />} />
          <Route path="/reflect/history" element={<ReflectHistory />} />
          <Route path="/u/:handle/reflect/history" element={<ReflectHistory />} />
          <Route path="/status" element={<Status />} />
          <Route path="/u/:handle/status" element={<Status />} />
          <Route path="/perf" element={<Perf />} />
          <Route path="/u/:handle/perf" element={<Perf />} />
          <Route path="/settings/ai" element={<SettingsAi />} />
          <Route path="/u/:handle/settings/ai" element={<SettingsAi />} />
          <Route path="/settings/ai/features" element={<AiFeaturesSettings />} />
          <Route path="/u/:handle/settings/ai/features" element={<AiFeaturesSettings />} />
          <Route path="/settings/prompts" element={<SettingsPrompts />} />
          <Route path="/u/:handle/settings/prompts" element={<SettingsPrompts />} />
          <Route path="/settings/agent-runs" element={<SettingsAgentRuns />} />
          <Route path="/u/:handle/settings/agent-runs" element={<SettingsAgentRuns />} />
          <Route path="/settings/context" element={<ContextSettings />} />
          <Route path="/u/:handle/settings/context" element={<ContextSettings />} />
          <Route path="/teams" element={<Teams />} />
          <Route path="/u/:handle/teams" element={<Teams />} />
          <Route path="/whatsapp" element={<WhatsApp />} />
          <Route path="/u/:handle/whatsapp" element={<WhatsApp />} />
          <Route path="/preferences" element={<Preferences />} />
          <Route path="/u/:handle/preferences" element={<Preferences />} />
          <Route path="/routines" element={<Routines />} />
          <Route path="/u/:handle/routines" element={<Routines />} />
          <Route path="/decisions" element={<Decisions />} />
          <Route path="/u/:handle/decisions" element={<Decisions />} />
          <Route path="/health" element={<Health />} />
          <Route path="/u/:handle/health" element={<Health />} />
          <Route path="/patterns" element={<Patterns />} />
          <Route path="/u/:handle/patterns" element={<Patterns />} />
          <Route path="/reconcile" element={<Navigate to="/attention?filter=recommendation" replace />} />
          <Route path="/evolution" element={<Navigate to="/attention?filter=recommendation" replace />} />
          {/* W076.4 day-7 surface flip: six top-level pages.
              Today is canonical at "/" (cockpit). Attention / People /
              Library / Recall land here. Old queue + ontology pages
              redirect to their successor under one of these six. */}
          <Route path="/attention" element={<Attention />} />
          <Route path="/u/:handle/attention" element={<Attention />} />
          <Route path="/people" element={<People />} />
          <Route path="/u/:handle/people" element={<People />} />
          <Route path="/library" element={<Library />} />
          <Route path="/u/:handle/library" element={<Library />} />
          <Route path="/recall" element={<Recall />} />
          <Route path="/u/:handle/recall" element={<Recall />} />
          <Route path="/weave" element={<Navigate to="/library" replace />} />
          <Route path="/library/:entity" element={<LibraryEntityRedirect />} />
          {/* Anything else: send to the root resolver rather than a
              blank screen. RootRoute handles auth/onboarding/workspace. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
  );
}

/** Preserves the :entity segment when redirecting the bare
 *  /library/:entity alias to the new tab-based Library page. */
function LibraryEntityRedirect() {
  const { entity } = useParams<{ entity: string }>();
  const mapped = (() => {
    const e = (entity || "").toLowerCase();
    if (["trips", "trip", "travel", "agenda"].includes(e)) return "trips";
    if (["money", "transactions", "subscriptions", "financial"].includes(e)) return "money";
    if (["places", "place", "locations"].includes(e)) return "places";
    if (["wishes", "things", "decisions", "routines", "reflect", "health", "timeline"].includes(e)) return e;
    return "trips";
  })();
  return <Navigate to={`/library?tab=${encodeURIComponent(mapped)}`} replace />;
}

/** Bare `/` resolver. Loading: hold. Not authenticated: render
 *  the Login canvas inline (no redirect, so the URL stays clean).
 *  Authenticated but not onboarded (or no handle): send to
 *  /onboarding. Onboarded with handle: send to their workspace URL. */
function RootRoute() {
  const auth = useAuth();
  if (auth.loading) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-slate-500 dark:text-slate-400">
        Loading…
      </div>
    );
  }
  if (!auth.authenticated) {
    return <Login />;
  }
  // Force the wizard for any user who hasn't completed it, even if a
  // handle was auto-derived from their email (which is the case for
  // users whose row was created during multi-tenant refactor testing
  // pre-0010 migration -- they had handles but never saw the wizard).
  if (!auth.user?.handle || !auth.user?.onboardedAt) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Navigate to={`/u/${encodeURIComponent(auth.user.handle)}`} replace />;
}

/** /u/:handle route. For now: if the URL handle matches the signed-in
 *  user's handle, render the Today cockpit. If it doesn't match, fall
 *  back to the user's own workspace (no cross-user viewing yet; that's
 *  a sharing feature for later). */
function UserWorkspace() {
  const auth = useAuth();
  const { handle } = useParams<{ handle: string }>();
  if (auth.loading) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-slate-500 dark:text-slate-400">
        Loading…
      </div>
    );
  }
  if (!auth.authenticated) {
    return <Navigate to="/login" replace />;
  }
  // Same gate as RootRoute: any user who hasn't onboarded gets routed
  // through the wizard, even if they navigated directly to /u/<handle>.
  if (!auth.user?.handle || !auth.user?.onboardedAt) {
    return <Navigate to="/onboarding" replace />;
  }
  const myHandle = auth.user.handle;
  if (handle && handle.toLowerCase() !== myHandle.toLowerCase()) {
    return <Navigate to={`/u/${encodeURIComponent(myHandle)}`} replace />;
  }
  // Day 7 (W076.4): default landing is the curated Today page,
  // not the raw engagement list. Today now hosts the delivered
  // brief hero + top-of-mind attention strip + curated cards.
  return <Today />;
}
